import { useMemo, useState } from "react";
import SyringeViz from "@/components/SyringeViz";

const ACTIVITY_OPTIONS = [
  { id: "sedentary", label: "Sedentary (little or no exercise)", factor: 0.92 },
  { id: "light", label: "Light (1–3 days/week)", factor: 1.0 },
  { id: "moderate", label: "Moderate (3–5 days/week)", factor: 1.05 },
  { id: "active", label: "Active (6–7 days/week)", factor: 1.1 },
  { id: "very_active", label: "Very Active (intense + physical job)", factor: 1.15 },
];

// Stepped base recommendation (mg/week) by current T level (ng/dL)
function baseDoseFromTLevel(t) {
  if (!t || t <= 0) return 0;
  if (t < 300) return 180;
  if (t < 450) return 150;
  if (t < 600) return 120;
  if (t < 750) return 80;
  return 0; // already in healthy range — TRT not indicated by this calculator
}

const inp =
  "bg-[#121212] border border-[#262626] text-white rounded-md px-4 py-3 focus:outline-none focus:border-[#FF8A3D] focus:ring-1 focus:ring-[#FF8A3D] transition-all font-mono-data text-sm";

export default function TRTCalculator() {
  // --- Section 1: Profile-based recommendation ---
  const [age, setAge] = useState(35);
  const [weightLbs, setWeightLbs] = useState(180);
  const [heightIn, setHeightIn] = useState(70);
  const [activityId, setActivityId] = useState("moderate");
  const [currentT, setCurrentT] = useState(350);

  // --- Section 2: Injection split ---
  const [weeklyMg, setWeeklyMg] = useState(100);
  const [vialMgPerMl, setVialMgPerMl] = useState(200);
  const [intervalDays, setIntervalDays] = useState(7);

  const activity = ACTIVITY_OPTIONS.find((a) => a.id === activityId) || ACTIVITY_OPTIONS[2];

  const bmi = useMemo(() => {
    const w = parseFloat(weightLbs);
    const h = parseFloat(heightIn);
    if (!w || !h) return null;
    const kg = w * 0.45359237;
    const m = h * 0.0254;
    return kg / (m * m);
  }, [weightLbs, heightIn]);

  const recommendation = useMemo(() => {
    const base = baseDoseFromTLevel(parseFloat(currentT));
    if (!base) return { weeklyMg: 0, weeklyMl: 0, alreadyHealthy: parseFloat(currentT) >= 750 };
    const raw = base * activity.factor;
    const rounded = Math.round(raw / 5) * 5;
    const capped = Math.min(rounded, 250);
    return { weeklyMg: capped, weeklyMl: capped / 200 };
  }, [currentT, activity]);

  const split = useMemo(() => {
    const wk = parseFloat(weeklyMg);
    const conc = parseFloat(vialMgPerMl);
    const interval = parseFloat(intervalDays);
    if (!wk || !conc || !interval) return null;
    const perInjMg = wk * (interval / 7);
    const perInjMl = perInjMg / conc;
    const insulinUnits = perInjMl * 100; // U-100 reading
    const dosesPerVial10mL = (conc * 10) / perInjMg; // standard 10 mL TRT vial
    return { perInjMg, perInjMl, insulinUnits, dosesPerVial10mL };
  }, [weeklyMg, vialMgPerMl, intervalDays]);

  const schedule = useMemo(() => {
    if (!split) return [];
    const interval = parseFloat(intervalDays);
    if (!interval) return [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return Array.from({ length: 8 }, (_, i) => {
      const d = new Date(today.getTime() + i * interval * 86400000);
      return {
        n: i + 1,
        date: d.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" }),
        mg: split.perInjMg,
        ml: split.perInjMl,
      };
    });
  }, [split, intervalDays]);

  // Trough-to-peak swing at steady state for T-cypionate (half-life ~8 days).
  // Standard PK: trough/peak = exp(-ln2 / t½ × interval). swing = 1 − trough/peak.
  const stability = useMemo(() => {
    const interval = parseFloat(intervalDays);
    if (!interval || interval <= 0) return null;
    const halfLifeDays = 8;
    const decay = Math.exp((-Math.LN2 / halfLifeDays) * interval);
    const swingPct = (1 - decay) * 100;
    let rating, color, advice;
    if (swingPct < 12) {
      rating = "Excellent stability";
      color = "#CCFF00";
      advice = "Trough-to-peak swing under 12% — minimal hormonal fluctuation. Daily / EOD micro-dosing tier.";
    } else if (swingPct < 25) {
      rating = "Good stability";
      color = "#00E5FF";
      advice = "~15–25% swing between doses. Most users feel stable on this schedule (E3.5D or twice-weekly).";
    } else if (swingPct < 40) {
      rating = "Moderate swings";
      color = "#FF8A3D";
      advice = "~25–40% drop trough-to-peak. Some users notice mid-cycle mood or libido dips. Consider splitting to E3.5D.";
    } else {
      rating = "Significant swings";
      color = "#FF3366";
      advice = "Over 40% drop between doses — many users report mid-week energy/libido lows on once-weekly cyp. Split to twice-weekly or E3.5D for tighter control.";
    }
    return { rating, color, advice, swingPct };
  }, [intervalDays]);

  return (
    <div data-testid="trt-calculator">
      {/* Intro */}
      <div className="mb-8">
        <div className="text-[10px] font-bold tracking-[0.25em] uppercase text-[#FF8A3D] mb-2">★ Hormone Replacement</div>
        <h2 className="font-heading text-3xl sm:text-4xl font-black mb-2">TRT Dosage Calculator</h2>
        <p className="text-[#A3A3A3] max-w-xl text-sm">Estimate a TRT starting protocol from your profile, then split the weekly dose across your preferred injection frequency.</p>
      </div>

      {/* ── Section 1: Recommendation ── */}
      <div className="bg-[#0A0A0A] border border-[#262626] rounded-lg p-6 sm:p-8 mb-8">
        <div className="text-xs font-bold tracking-[0.3em] uppercase text-[#FF8A3D] mb-5">/ STEP 1 — RECOMMENDATION</div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mb-6">
          <Field label="Age (years)">
            <input data-testid="trt-age" type="text" inputMode="numeric" value={age} onChange={(e) => setAge(e.target.value)} className={`${inp} w-full`} />
          </Field>
          <Field label="Weight (lbs)">
            <input data-testid="trt-weight" type="text" inputMode="decimal" value={weightLbs} onChange={(e) => setWeightLbs(e.target.value)} className={`${inp} w-full`} />
          </Field>
          <Field label="Height (inches)">
            <input data-testid="trt-height" type="text" inputMode="decimal" value={heightIn} onChange={(e) => setHeightIn(e.target.value)} className={`${inp} w-full`} />
          </Field>
          <Field label="Current T level (ng/dL)" hint="Normal range: 300–1,000 ng/dL">
            <input data-testid="trt-current-t" type="text" inputMode="numeric" value={currentT} onChange={(e) => setCurrentT(e.target.value)} className={`${inp} w-full`} />
          </Field>
          <Field label="Activity level">
            <select data-testid="trt-activity" value={activityId} onChange={(e) => setActivityId(e.target.value)} className={`${inp} w-full`}>
              {ACTIVITY_OPTIONS.map((a) => (
                <option key={a.id} value={a.id}>{a.label}</option>
              ))}
            </select>
          </Field>
          <Field label="BMI" hint="Auto-calculated">
            <div data-testid="trt-bmi" className={`${inp} w-full select-none`}>
              {bmi != null ? `${bmi.toFixed(1)} kg/m²` : "—"}
              {bmi != null && (
                <span className="ml-3 text-xs text-[#A3A3A3]">{bmiCategory(bmi)}</span>
              )}
            </div>
          </Field>
        </div>

        {/* Result */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-px bg-[#262626] border border-[#262626] rounded-lg overflow-hidden">
          <TrtStat
            k="Recommended dose"
            v={recommendation.weeklyMg ? `${recommendation.weeklyMg} mg/wk` : "—"}
            highlight
            testid="trt-rec-dose"
          />
          <TrtStat
            k="Weekly volume @ 200 mg/mL"
            v={recommendation.weeklyMl ? `${recommendation.weeklyMl.toFixed(2)} mL/wk` : "—"}
            testid="trt-rec-volume"
          />
          <TrtStat
            k="Current T"
            v={`${currentT || "—"} ng/dL`}
            testid="trt-rec-current"
          />
        </div>

        {recommendation.alreadyHealthy && (
          <div data-testid="trt-already-healthy" className="mt-4 bg-[#FF8A3D]/10 border border-[#FF8A3D]/40 rounded p-4 text-sm text-[#FF8A3D]">
            ⚑ Your T is already in the upper-normal range (≥ 750 ng/dL). TRT is generally not indicated. Talk to a physician about optimization options other than exogenous testosterone.
          </div>
        )}

        <button
          type="button"
          data-testid="trt-apply-recommendation"
          disabled={!recommendation.weeklyMg}
          onClick={() => setWeeklyMg(recommendation.weeklyMg)}
          className="mt-5 inline-flex items-center gap-2 px-5 py-2.5 rounded-md font-mono-data text-xs font-bold tracking-wider uppercase bg-[#FF8A3D] text-black disabled:bg-[#262626] disabled:text-[#A3A3A3] transition-colors hover:bg-[#FF7A28]"
        >
          ↓ Use this dose in Step 2
        </button>
      </div>

      {/* ── Section 2: Injection split ── */}
      <div className="bg-[#0A0A0A] border border-[#262626] rounded-lg p-6 sm:p-8 mb-8">
        <div className="text-xs font-bold tracking-[0.3em] uppercase text-[#FF8A3D] mb-5">/ STEP 2 — INJECTION SPLIT</div>

        {split && (
          <div className="mb-6">
            <SyringeViz units={split.insulinUnits} maxUnits={100} color="#FF8A3D" />
            {split.insulinUnits > 100 && (
              <div data-testid="trt-overflow" className="mt-4 bg-[#FF3366]/10 border border-[#FF3366]/40 rounded p-4 text-sm text-[#FF3366]">
                ⚠ This per-injection volume exceeds a 1 mL insulin syringe. Use a 3 mL syringe, or inject more frequently to lower the per-dose volume.
              </div>
            )}
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 mb-6">
          <Field label="Weekly dosage (mg)">
            <input data-testid="trt-weekly-mg" type="text" inputMode="decimal" value={weeklyMg} onChange={(e) => setWeeklyMg(e.target.value)} className={`${inp} w-full`} />
          </Field>
          <Field label="Vial strength (mg/mL)" hint="Cyp/Enanthate is typically 200">
            <input data-testid="trt-vial-strength" type="text" inputMode="decimal" value={vialMgPerMl} onChange={(e) => setVialMgPerMl(e.target.value)} className={`${inp} w-full`} />
          </Field>
          <Field label="Injection interval (days)" hint="3.5 for E3.5D, 7 for weekly, 2 for EOD">
            <input data-testid="trt-interval" type="text" inputMode="decimal" value={intervalDays} onChange={(e) => setIntervalDays(e.target.value)} className={`${inp} w-full`} />
          </Field>
        </div>

        {split && (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-px bg-[#262626] border border-[#262626] rounded-lg overflow-hidden mb-6">
              <TrtStat k="Per injection (mg)" v={`${split.perInjMg.toFixed(1)} mg`} testid="trt-per-mg" />
              <TrtStat k="Per injection (mL)" v={`${split.perInjMl.toFixed(3)} mL`} testid="trt-per-ml" />
              <TrtStat k="On U-100 insulin" v={`${split.insulinUnits.toFixed(1)} u`} highlight testid="trt-per-units" />
              <TrtStat k="10 mL vial lasts" v={`${split.dosesPerVial10mL.toFixed(1)} doses`} testid="trt-vial-doses" />
            </div>

            {/* Trough/peak stability */}
            {stability && (
              <div
                data-testid="trt-stability"
                className="mb-6 px-5 py-4 rounded-md font-mono-data"
                style={{ background: `${stability.color}12`, border: `1px solid ${stability.color}55` }}
              >
                <div className="flex items-baseline justify-between gap-3 mb-2">
                  <span className="text-[10px] font-bold tracking-[0.25em] uppercase" style={{ color: stability.color }}>
                    Trough → peak stability
                  </span>
                  <span data-testid="trt-stability-swing" className="text-xs text-[#A3A3A3]">
                    ~{stability.swingPct.toFixed(0)}% swing
                  </span>
                </div>
                <div data-testid="trt-stability-rating" className="font-bold text-base mb-1" style={{ color: stability.color }}>
                  {stability.rating}
                </div>
                <p className="text-xs text-[#A3A3A3] leading-relaxed">{stability.advice}</p>
                <p className="text-[10px] text-[#A3A3A3] mt-2 italic">Estimate based on T-cypionate half-life (~8 days). T-enanthate is similar; T-propionate has much shorter half-life (~2 days) and requires EOD dosing.</p>
              </div>
            )}

            {/* Schedule */}
            <div data-testid="trt-schedule" className="bg-[#121212] border border-[#262626] rounded-md p-5">
              <div className="text-[10px] font-bold tracking-[0.25em] uppercase text-[#FF8A3D] mb-3">Next 8 injections</div>
              <table className="w-full text-sm font-mono-data">
                <thead>
                  <tr className="text-[10px] tracking-widest uppercase text-[#A3A3A3] border-b border-[#262626]">
                    <th className="text-left py-2 font-semibold">#</th>
                    <th className="text-left py-2 font-semibold">Date</th>
                    <th className="text-right py-2 font-semibold">mg</th>
                    <th className="text-right py-2 font-semibold">mL</th>
                  </tr>
                </thead>
                <tbody>
                  {schedule.map((s) => (
                    <tr key={s.n} className="border-b border-[#262626]/40 last:border-0">
                      <td className="py-2 text-[#A3A3A3]">{s.n}</td>
                      <td className="py-2 text-white">{s.date}</td>
                      <td className="py-2 text-right text-[#FF8A3D] font-bold">{s.mg.toFixed(1)}</td>
                      <td className="py-2 text-right text-white">{s.ml.toFixed(3)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      {/* Safety disclaimer */}
      <div data-testid="trt-disclaimer" className="bg-[#0A0A0A] border border-[#FF3366]/30 rounded-lg p-5">
        <div className="text-[10px] font-bold tracking-[0.25em] uppercase text-[#FF3366] mb-2">⚠ Important disclaimer</div>
        <p className="text-xs text-[#A3A3A3] leading-relaxed">
          This calculator provides estimates for educational purposes only. <strong className="text-white">It is not medical advice.</strong> TRT requires proper bloodwork, monitoring (estradiol, hematocrit, PSA, lipids), and physician oversight. Individual needs vary significantly based on labs, symptoms, and medical history. Always consult a qualified healthcare provider before starting any testosterone protocol.
        </p>
      </div>
    </div>
  );
}

function bmiCategory(bmi) {
  if (bmi < 18.5) return "underweight";
  if (bmi < 25) return "normal";
  if (bmi < 30) return "overweight";
  return "obese";
}

function Field({ label, hint, children }) {
  return (
    <div>
      <label className="text-sm font-semibold text-[#A3A3A3] mb-2 block">{label}</label>
      {children}
      {hint && <p className="text-[10px] text-[#A3A3A3] mt-2 font-mono-data">{hint}</p>}
    </div>
  );
}

function TrtStat({ k, v, highlight, testid }) {
  return (
    <div data-testid={testid} className={`p-4 ${highlight ? "bg-[#FF8A3D]" : "bg-[#0A0A0A]"}`}>
      <div className={`text-[10px] tracking-widest uppercase ${highlight ? "text-black/70" : "text-[#A3A3A3]"}`}>{k}</div>
      <div className={`font-mono-data font-bold text-lg mt-1 ${highlight ? "text-black" : "text-white"}`}>{v}</div>
    </div>
  );
}
